import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useVerifyResetToken, useResetPassword, usePasswordPolicy } from '@/containers/Login/hooks/useAuth';
import { ROUTES } from '@/routes/config';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const verifyToken = useVerifyResetToken();
  const resetPassword = useResetPassword();
  const { data: policy } = usePasswordPolicy();

  useEffect(() => {
    if (token) {
      verifyToken.mutate(token);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const rules = useMemo(() => {
    const p = policy ?? {
      min_length: 12,
      require_uppercase: true,
      require_lowercase: true,
      require_number: true,
      require_special: true,
    };
    const r: { label: string; test: (pw: string) => boolean }[] = [
      { label: `At least ${p.min_length} characters`, test: (pw) => pw.length >= p.min_length },
    ];
    if (p.require_uppercase) r.push({ label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) });
    if (p.require_lowercase) r.push({ label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) });
    if (p.require_number) r.push({ label: 'One number', test: (pw) => /[0-9]/.test(pw) });
    if (p.require_special) r.push({ label: 'One special character (!@#$%^&*)', test: (pw) => /[!@#$%^&*]/.test(pw) });
    return r;
  }, [policy]);

  const passwordErrors = useMemo(
    () => rules.filter((rule) => !rule.test(newPassword)),
    [newPassword, rules]
  );

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isPasswordValid = newPassword.length > 0 && passwordErrors.length === 0;
  const isFormValid = isPasswordValid && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await resetPassword
      .mutateAsync({ token, new_password: newPassword })
      .then(() => setResetSuccess(true))
      .catch(() => {});
  };

  if (!token) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Invalid Link</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This password reset link is invalid or missing.
            </Typography>
            <Link component={RouterLink} to={ROUTES.FORGOT_PASSWORD} variant="body2">
              Request a new reset link
            </Link>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (verifyToken.isPending) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Verifying reset link...</Typography>
        </Box>
      </Container>
    );
  }

  if (verifyToken.error) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Link Expired</Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              {(verifyToken.error as Error).message || 'This reset link has expired or is invalid.'}
            </Alert>
            <Link component={RouterLink} to={ROUTES.FORGOT_PASSWORD} variant="body2">
              Request a new reset link
            </Link>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (resetSuccess) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Password Reset</Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your password has been reset successfully.
            </Alert>
            <Link component={RouterLink} to={ROUTES.LOGIN} variant="body2">
              Sign In
            </Link>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
            Set New Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Enter your new password below.
          </Typography>

          {resetPassword.error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {(resetPassword.error as Error).message || 'Password reset failed. Please try again.'}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={resetPassword.isPending}
            />

            {newPassword.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }}>
                {rules.map((rule) => {
                  const passed = rule.test(newPassword);
                  return (
                    <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {passed ? (
                        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                      <Typography variant="caption" color={passed ? 'success.main' : 'error.main'}>
                        {rule.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '}
              disabled={resetPassword.isPending}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 1 }}
              disabled={!isFormValid || resetPassword.isPending}
              startIcon={resetPassword.isPending ? <CircularProgress size={20} /> : null}
            >
              {resetPassword.isPending ? 'Resetting...' : 'Reset Password'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
